/* External */
import { memo, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';

/* Components */
import FocusableButton from '../FocusableButton';

/* Utils */
import withCursorVisibilityFilterFocusable from '../../hocs/withCursorVisibilityFilter.hoc';
import { noOp } from '../../utils/functions';
import { getIconFromBlockStatus } from '../../utils/iconBlockUtils';
import { gaService } from '../../services/gaService';
import { REPORT_GA } from '../../utils/constants';
import { Direction } from '../../utils/spatialNavigation';

/* Style */
import './PlayResumeButton.scss';

function PlayResumeButton({
  children,
  className,
  isNonEntitledContent,
  isContentBlockedByParentalControl,
  progress,
  duration,
  hasFocusedChild,
  focusRestorable,
  onBecameFocused,
  onPress,
  blockDownArrow,
  blockUpArrow,
}) {
  const progressOffset = useMemo(() => {
    if (progress >= duration * 0.95) {
      return 0;
    }

    return progress;
  }, [duration, progress]);

  const progressStyle = useMemo(() => {
    const ratio = progressOffset / duration;
    return {
      width: `${(ratio > 1 ? 1 : ratio) * 100}%`,
    };
  }, [duration, progressOffset]);

  const handleEnterPress = useCallback(() => {
    onPress();
    gaService.sendEvent({
      action: progress > 0
        ? REPORT_GA.GA_EVENT_ACTION_PDP_RESUME_CONTENT_CLICK
        : REPORT_GA.GA_EVENT_ACTION_PDP_WATCH_NOW_CLICK,
      label: progress > 0
        ? REPORT_GA.GA_EVENT_LABEL_PDP_RESUME_CONTENT_CLICK
        : REPORT_GA.GA_EVENT_LABEL_PDP_WATCH_NOW_CLICK,
      category: REPORT_GA.GA_EVENT_CATEGORY_PDP,
    });
  }, [onPress, progress]);

  const handleArrowPressButton = useCallback((direction) => {
    if ((blockUpArrow && direction === Direction.UP) || direction === Direction.LEFT) {
      return false;
    }
    return !(blockDownArrow && direction === Direction.DOWN);
  }, [blockDownArrow, blockUpArrow]);

  const isLoadingIcon = isNonEntitledContent.constructor === Object;

  const iconName = getIconFromBlockStatus(isNonEntitledContent, isContentBlockedByParentalControl);

  return (
    <FocusableButton
      className={`dtv-html5-c-play-resume-button ${hasFocusedChild ? 'dtv-html5-c-play-resume-button--focused' : ''
      } ${progressOffset > 0 ? 'dtv-html5-c-play-resume-button--with-progress' : ''
      } ${isLoadingIcon ? 'dtv-html5-c-play-resume-button--loading' : ''
      } ${className}`}
      icon={iconName}
      primary
      bordered
      focusRestorable={focusRestorable}
      onBecameFocused={onBecameFocused}
      onEnterPress={handleEnterPress}
      onArrowPress={handleArrowPressButton}
    >
      {children}
      {progressOffset > 0 && (
        <div className="dtv-html5-c-play-resume-button__progress">
          <div className="dtv-html5-c-play-resume-button__content-progress">
            <div className="dtv-html5-c-play-resume-button__progress-seen" style={progressStyle} />
          </div>
        </div>
      )}
    </FocusableButton>
  );
}

PlayResumeButton.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  isNonEntitledContent: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.shape({}),
  ]),
  isContentBlockedByParentalControl: PropTypes.bool,
  progress: PropTypes.number,
  duration: PropTypes.number,
  hasFocusedChild: PropTypes.bool.isRequired,
  focusRestorable: PropTypes.bool,
  blockDownArrow: PropTypes.bool,
  blockUpArrow: PropTypes.bool,
  onBecameFocused: PropTypes.func,
  onPress: PropTypes.func,
};

PlayResumeButton.defaultProps = {
  children: '',
  className: '',
  isNonEntitledContent: false,
  isContentBlockedByParentalControl: false,
  focusRestorable: false,
  progress: 0,
  duration: 0,
  blockDownArrow: false,
  blockUpArrow: true,
  onBecameFocused: noOp,
  onPress: noOp,
};

export default memo(withCursorVisibilityFilterFocusable(
  { trackChildren: true },
)(memo(PlayResumeButton)));
